import type { WalkabilityPlaceMetrics } from '../../services/walkabilityTransportMetrics';
import { CLASS_ORDER } from '../../services/walkabilityTransportMetrics';
import { getAuthoredOverview, renderAuthoredNarrativeBundle } from './authoredNarrativeRender';
import { transportMapMethodology } from './transportMapMethodology';

const CHART_ID = 'walkability';
const VIEW_ID = 'default';

function categoryDistributionSentence(metrics: WalkabilityPlaceMetrics): string {
  if (metrics.tractCount <= 0) return '';
  const parts: string[] = [];
  for (const cls of CLASS_ORDER) {
    if (cls === 'Unknown') continue;
    const n = metrics.counts[cls] ?? 0;
    if (n > 0) {
      parts.push(`${n} tract${n === 1 ? '' : 's'} in the ${cls} range`);
    }
  }
  if (!parts.length) return '';
  return ` ${parts.slice(0, 3).join('; ')}${parts.length > 3 ? '; …' : ''}.`;
}

export function buildWalkabilityTransportAuthoredBundle(
  locationName: string,
  metrics: WalkabilityPlaceMetrics | null
) {
  const median =
    metrics?.medianPercentile != null ? Number(metrics.medianPercentile.toFixed(1)) : null;
  const populationWeightedAverage =
    metrics?.avgPercentile != null ? Number(metrics.avgPercentile.toFixed(1)) : null;
  const highlyCount = metrics?.counts['Highly walkable (80–100)'] ?? 0;
  const highlyShare =
    metrics && metrics.tractCount > 0
      ? Number(((highlyCount / metrics.tractCount) * 100).toFixed(1))
      : null;

  const tokens = {
    locationName,
    tractCount: metrics?.tractCount ?? null,
    medianWalkabilityPercentile: median,
    populationWeightedAveragePercentile: populationWeightedAverage,
    highlyWalkableCount: highlyCount,
    highlyWalkableShare: highlyShare,
    categoryDistributionSentence: metrics ? categoryDistributionSentence(metrics) : '',
    comparisonSentence: '',
    sourceVintage: 'EPA National Walkability Index (June 2021 methodology)',
  };

  const bundle = renderAuthoredNarrativeBundle(CHART_ID, VIEW_ID, tokens);
  const structured = transportMapMethodology('walkability');
  return {
    ...bundle,
    methodology: {
      primarySources: structured.primarySources,
      calculation: structured.calculation,
      limitations: structured.limitations,
      technicalDetails: structured.technicalDetails,
    },
  };
}

export function walkabilityTransportOverviewDescription(): string {
  return getAuthoredOverview(
    CHART_ID,
    VIEW_ID,
    'EPA National Walkability Index at tract scale for the selected geography.'
  );
}
